import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  Home, Briefcase, Users, Calendar, DollarSign, ReceiptText, PiggyBank,
  UserPlus, FileText, BarChart3, MessageSquare, Settings, LogOut, Scale,
  Menu, Moon, Sun, Search, ClipboardList, Receipt, Activity, CalendarClock,
  UsersRound, ShieldCheck, Lock, X, Loader, UserCircle, Clock,
} from 'lucide-react';
import { useRoute } from '../../lib/router';
import { useAuth } from '../../contexts/AuthContext';
import { useFirm } from '../../contexts/FirmContext';
import { usePermissions } from '../../contexts/PermissionsContext';
import { usePlan } from '../../contexts/PlanContext';
import { useTimer } from '../../contexts/TimerContext';
import { classNames, fullName, initials } from '../../lib/formatters';
import { globalSearch } from '../../lib/db';
import TrialBanner from '../TrialBanner';
import UpgradeModal from '../UpgradeModal';
import NotificationBell from '../NotificationBell';

// Sidebar nav, organized into logical sections. Per item:
//   feature:    minimum plan required (checked via hasFeature). Absent = always available.
//   permission: RBAC permission key (checked via can). Absent = no permission gate.
//   tour:       optional product-tour anchor (data-tour=).
//
// Section rendering logic in the JSX below:
//   * Trial firm  → out-of-plan items shown with a lock icon (preview).
//   * Paid firm   → out-of-plan items are hidden entirely.
const NAV_SECTIONS = [
  {
    label: null, // top section — no header
    items: [
      { label: 'Dashboard',  path: '/',          icon: Home,         tour: 'nav-dashboard' },
    ],
  },
  {
    label: 'Work',
    items: [
      { label: 'Case Files', path: '/matters',   icon: Briefcase,    tour: 'nav-matters' },
      { label: 'Contacts',   path: '/contacts',  icon: Users,        tour: 'nav-contacts' },
      { label: 'Calendar',   path: '/calendar',  icon: Calendar },
      { label: 'Deadlines',  path: '/deadlines', icon: CalendarClock },
      { label: 'Tasks',      path: '/tasks',     icon: ClipboardList },
      { label: 'Documents',  path: '/documents', icon: FileText },
      { label: 'Messages',   path: '/messages',  icon: MessageSquare },
    ],
  },
  {
    label: 'Time & Billing',
    items: [
      { label: 'Time',           path: '/time',     icon: DollarSign,                              tour: 'nav-time'  },
      { label: 'Billing',        path: '/billing',  icon: ReceiptText,                             tour: 'nav-billing' },
      { label: 'Expenses',       path: '/expenses', icon: Receipt },
      { label: 'Client Account', path: '/trust',    icon: PiggyBank, feature: 'trust',             tour: 'nav-trust' },
    ],
  },
  {
    label: 'Growth & Risk',
    items: [
      { label: 'Intake',         path: '/intake',   icon: UserPlus,    feature: 'intake',          tour: 'nav-intake' },
      { label: 'Conflict',       path: '/conflict', icon: ShieldCheck, feature: 'conflict_check' },
    ],
  },
  {
    label: 'Insights',
    items: [
      { label: 'Reports',        path: '/reports',  icon: BarChart3,   feature: 'reports',   permission: 'finance.view_firm' },
      { label: 'Activity',       path: '/activity', icon: Activity,                          permission: 'activity.view' },
    ],
  },
  {
    label: 'Team',
    items: [
      { label: 'Employees',      path: '/employees',icon: UsersRound,  feature: 'employees' },
    ],
  },
];

// Admin-only nav items shown below the main list
const ADMIN_NAV = [
  { label: 'Roles',     path: '/roles',      icon: ShieldCheck,   feature: 'roles' },
  { label: 'Settings',  path: '/settings',   icon: Settings },
];

// Bottom tab bar — 4 primary destinations + "More" drawer trigger.
// The "Timer" tab is special-cased below to show the live elapsed time
// when a billable timer is running.
const BOTTOM_TABS = [
  { label: 'Home',      path: '/',         icon: Home },
  { label: 'Cases',     path: '/matters',  icon: Briefcase },
  { label: 'Timer',     path: '/time',     icon: Clock,     special: 'timer' },
  { label: 'Contacts',  path: '/contacts', icon: Users },
];

// "My Profile" is a dynamic path (/employees/:id) injected per-user below.

// employeeRecord is passed from AppInner (App.jsx) which already resolved the
// 3-tier waterfall. AppShell reads from it directly so it doesn't run its own
// duplicate DB queries on every page load.
export default function AppShell({ children, employeeRecord = null }) {
  const [{ pathname }, navigate] = useRoute();
  const { userProfile, logout } = useAuth();
  const { firm, isTrial } = useFirm();
  const { can } = usePermissions();
  const { hasFeature } = usePlan();
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  // mobileOpen controls the slide-in drawer on small screens
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dark, setDark] = useState(() => localStorage.getItem('theme') === 'dark');
  const timer = useTimer();

  // Format ms → m:ss / h:mm:ss for the bottom-nav timer label.
  const timerLabel = (() => {
    if (!timer.running) return 'Timer';
    const total = Math.floor(timer.elapsedMs / 1000);
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;
    const pad = (n) => (n < 10 ? `0${n}` : `${n}`);
    return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
  })();

  // Fetch the actual employees.id UUID for the logged-in employee so the
  // "My Profile" nav link points to the correct route.
  // NOTE: userProfile.employee_id is the "EMP000001" display string — it is
  // NOT the employees table primary key. We must look up by auth user UUID.
  // Derived directly from the employeeRecord prop resolved by App.jsx —
  // no extra DB queries needed here.
  const myEmployeeId = employeeRecord?.id ?? null;
  const myPhotoUrl   = employeeRecord?.profile_photo_url ?? null;

  // ── Global search ───────────────────────────────────────────────────────────
  const [searchQuery,   setSearchQuery]   = useState('');
  const [searchResults, setSearchResults] = useState(null); // null = closed
  const [searchLoading, setSearchLoading] = useState(false);
  const searchRef   = useRef(null);
  const debounceRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setSearchResults(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSearchChange = useCallback((e) => {
    const val = e.target.value;
    setSearchQuery(val);
    clearTimeout(debounceRef.current);

    if (!val.trim() || val.trim().length < 2) {
      setSearchResults(null);
      setSearchLoading(false);
      return;
    }

    if (!firm?.id) { setSearchLoading(false); return; }

    setSearchLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await globalSearch(firm?.id, val.trim());
        setSearchResults(res);
      } catch (err) {
        console.error('[GlobalSearch] error:', err);
        setSearchResults({ matters: [], contacts: [], documents: [] });
      } finally {
        setSearchLoading(false);
      }
    }, 300);
  }, [firm?.id]);

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Escape') { setSearchResults(null); setSearchQuery(''); }
  };

  const clearSearch = () => { setSearchQuery(''); setSearchResults(null); };

  const goToResult = (path) => { navigate(path); clearSearch(); setMobileOpen(false); };

  const hasResults = searchResults &&
    (searchResults.matters.length || searchResults.contacts.length || searchResults.documents.length);

  useEffect(() => {
    const root = document.documentElement;
    if (dark) { root.classList.add('dark'); localStorage.setItem('theme', 'dark'); }
    else      { root.classList.remove('dark'); localStorage.setItem('theme', 'light'); }
  }, [dark]);

  const activePath = (p) => (p === '/' ? pathname === '/' : pathname.startsWith(p));

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 dark:bg-slate-950 dark:text-slate-100">

      {/* Mobile backdrop — closes sidebar when tapped */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-20 bg-black/40 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={classNames(
          'fixed inset-y-0 left-0 z-30 flex flex-col border-r border-slate-200 bg-white transition-all duration-200',
          'dark:border-slate-800 dark:bg-slate-900',
          // Desktop: respect collapsed/expanded widths; always visible
          collapsed ? 'lg:w-16' : 'lg:w-60',
          // Mobile: always full-width drawer, slide in/out
          'w-60',
          mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0',
        )}
      >
        <div className={classNames('flex items-center gap-2 px-4 py-4 border-b border-slate-100 dark:border-slate-800', collapsed && 'lg:justify-center lg:px-0')}>
          {/* Show firm logo when set; fall back to indigo Scale icon */}
          {firm?.logo_url ? (
            <img
              src={firm.logo_url}
              alt={firm.name || 'Firm logo'}
              className="w-8 h-8 rounded-lg object-contain flex-shrink-0 bg-white p-0.5 border border-slate-200 dark:border-slate-700"
            />
          ) : (
            <div className="w-8 h-8 rounded-lg bg-indigo-600 flex items-center justify-center flex-shrink-0">
              <Scale size={18} className="text-white" />
            </div>
          )}
          {/* Show firm name: always on mobile, only when not collapsed on desktop */}
          <div className={classNames('min-w-0', collapsed && 'lg:hidden')}>
            <p className="text-sm font-semibold truncate">{firm?.name || 'Your Firm'}</p>
            <p className="text-xs text-slate-500 dark:text-slate-400 truncate">DocketFlow</p>
          </div>
        </div>

        <nav className="flex-1 px-2 py-2 space-y-0.5 overflow-y-auto">
          {/* My Profile — only for employees (users with an employees row).
              Path is dynamic per-user so it can't live in the static
              NAV_SECTIONS array. Sits right under Dashboard for easy reach. */}
          {myEmployeeId && (
            <button
              onClick={() => { navigate(`/employees/${myEmployeeId}`); setMobileOpen(false); }}
              title={collapsed ? 'My Profile' : undefined}
              className={classNames(
                'w-full flex items-center gap-3 rounded-lg px-3 py-1.5 text-sm transition',
                pathname === `/employees/${myEmployeeId}`
                  ? 'bg-indigo-50 text-indigo-700 font-medium dark:bg-indigo-500/10 dark:text-indigo-300'
                  : 'text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800',
                collapsed && 'lg:justify-center lg:px-0',
              )}
            >
              {myPhotoUrl ? (
                <img src={myPhotoUrl} alt="" className="w-4 h-4 rounded-full object-cover flex-shrink-0" />
              ) : (
                <UserCircle size={16} className="flex-shrink-0" />
              )}
              <span className={classNames('flex-1 text-left', collapsed && 'lg:hidden')}>
                My Profile
              </span>
              {!employeeRecord?.profile_completed_at && (
                <span
                  className={classNames(
                    'w-1.5 h-1.5 rounded-full bg-amber-500 flex-shrink-0',
                    collapsed && 'lg:hidden',
                  )}
                  title="Profile incomplete"
                />
              )}
            </button>
          )}

          {NAV_SECTIONS.map((section, si) => {
            // Filter logic per item:
            //   1. Permission gate (always enforced)
            //   2. Plan gate — TRIAL: keep & show lock | PAID: hide entirely
            const visibleItems = section.items.filter((item) => {
              if (item.permission && !can(item.permission)) return false;
              if (!item.feature) return true;
              if (hasFeature(item.feature)) return true;
              // Out-of-plan: show during trial (preview), hide once paid.
              return isTrial;
            });
            if (visibleItems.length === 0) return null;
            return (
              <div key={si} className={si > 0 ? 'pt-2' : ''}>
                {section.label && (
                  <p className={classNames(
                    'px-3 pt-1 pb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500',
                    collapsed && 'lg:hidden',
                  )}>
                    {section.label}
                  </p>
                )}
                {visibleItems.map((item) => {
                  const Icon   = item.icon;
                  const active = activePath(item.path);
                  // Only "locked" during trial — paid users never see locked items.
                  const locked = item.feature && !hasFeature(item.feature);
                  return (
                    <button
                      key={item.path}
                      onClick={() => { navigate(item.path); setMobileOpen(false); }}
                      title={collapsed ? item.label : undefined}
                      data-tour={item.tour}
                      className={classNames(
                        'w-full flex items-center gap-3 rounded-lg px-3 py-1.5 text-sm transition',
                        active
                          ? 'bg-indigo-50 text-indigo-700 font-medium dark:bg-indigo-500/10 dark:text-indigo-300'
                          : locked
                            ? 'text-slate-400 hover:bg-slate-100 dark:text-slate-500 dark:hover:bg-slate-800'
                            : 'text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800',
                        collapsed && 'lg:justify-center lg:px-0',
                      )}
                    >
                      <Icon size={16} className="flex-shrink-0" />
                      <span className={classNames('flex-1 text-left', collapsed && 'lg:hidden')}>
                        {item.label}
                      </span>
                      {locked && (
                        <Lock size={11} className={classNames('text-slate-400 flex-shrink-0', collapsed && 'lg:hidden')} />
                      )}
                    </button>
                  );
                })}
              </div>
            );
          })}

          {/* Admin section — visible to anyone with settings.view (Admin, Partner, IT) */}
          {can('settings.view') && (() => {
            // Same trial-vs-paid plan-gating as the main nav above.
            const visibleAdmin = ADMIN_NAV.filter((item) => {
              if (!item.feature) return true;
              if (hasFeature(item.feature)) return true;
              return isTrial;
            });
            if (visibleAdmin.length === 0) return null;
            return (
              <div className="pt-2">
                <p className={classNames('px-3 pt-1 pb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500', collapsed && 'lg:hidden')}>
                  Admin
                </p>
                {visibleAdmin.map((item) => {
                  const Icon   = item.icon;
                  const active = activePath(item.path);
                  const locked = item.feature && !hasFeature(item.feature);
                  return (
                    <button
                      key={item.path}
                      onClick={() => { navigate(item.path); setMobileOpen(false); }}
                      title={collapsed ? item.label : undefined}
                      className={classNames(
                        'w-full flex items-center gap-3 rounded-lg px-3 py-1.5 text-sm transition',
                        active
                          ? 'bg-indigo-50 text-indigo-700 font-medium dark:bg-indigo-500/10 dark:text-indigo-300'
                          : locked
                            ? 'text-slate-400 hover:bg-slate-100 dark:text-slate-500 dark:hover:bg-slate-800'
                            : 'text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800',
                        collapsed && 'lg:justify-center lg:px-0',
                      )}
                    >
                      <Icon size={16} className="flex-shrink-0" />
                      <span className={classNames('flex-1 text-left', collapsed && 'lg:hidden')}>
                        {item.label}
                      </span>
                      {locked && (
                        <Lock size={11} className={classNames('text-slate-400 flex-shrink-0', collapsed && 'lg:hidden')} />
                      )}
                    </button>
                  );
                })}
              </div>
            );
          })()}
        </nav>

        <div className="border-t border-slate-100 dark:border-slate-800 p-3 space-y-2 pb-20 lg:pb-3">
          {/* My Profile — only for users who have a linked employee record */}
          {myEmployeeId && (
            <button
              onClick={() => { navigate(`/employees/${myEmployeeId}`); setMobileOpen(false); }}
              title={collapsed ? 'My Profile' : undefined}
              className={classNames(
                'w-full flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition',
                pathname.startsWith(`/employees/${myEmployeeId}`)
                  ? 'bg-indigo-50 text-indigo-700 font-medium dark:bg-indigo-500/10 dark:text-indigo-300'
                  : 'text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800',
                collapsed && 'lg:justify-center lg:px-0',
              )}
            >
              <UserCircle size={18} className="flex-shrink-0" />
              <span className={classNames(collapsed && 'lg:hidden')}>My Profile</span>
            </button>
          )}
          <button
            onClick={() => setDark((v) => !v)}
            className={classNames(
              'w-full flex items-center gap-3 rounded-lg px-3 py-2 text-sm hover:bg-slate-100 dark:hover:bg-slate-800',
              collapsed && 'lg:justify-center lg:px-0',
            )}
            title={collapsed ? (dark ? 'Light mode' : 'Dark mode') : undefined}
          >
            {dark ? <Sun size={18} /> : <Moon size={18} />}
            <span className={classNames(collapsed && 'lg:hidden')}>{dark ? 'Light mode' : 'Dark mode'}</span>
          </button>
          <button
            onClick={logout}
            className={classNames(
              'w-full flex items-center gap-3 rounded-lg px-3 py-2 text-sm text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800',
              collapsed && 'lg:justify-center lg:px-0',
            )}
            title={collapsed ? 'Sign out' : undefined}
          >
            <LogOut size={18} />
            <span className={classNames(collapsed && 'lg:hidden')}>Sign out</span>
          </button>
        </div>
      </aside>

      {/* Main — on mobile no sidebar padding; on desktop respect collapsed/expanded */}
      <div className={classNames('transition-all duration-200', collapsed ? 'lg:pl-16' : 'lg:pl-60')}>
        {/* Top bar */}
        <header className="sticky top-0 z-20 flex items-center gap-4 px-4 sm:px-6 py-3 bg-white/80 backdrop-blur border-b border-slate-200 dark:bg-slate-900/80 dark:border-slate-800">
          <button
            onClick={() => {
              // On mobile: open drawer; on desktop: collapse/expand sidebar
              if (window.innerWidth < 1024) setMobileOpen((v) => !v);
              else setCollapsed((v) => !v);
            }}
            className="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-800"
            title="Toggle sidebar"
          >
            <Menu size={18} />
          </button>
          {/* ── Global search ── */}
          <div className="relative flex-1 max-w-md" ref={searchRef}>
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <input
              value={searchQuery}
              onChange={handleSearchChange}
              onKeyDown={handleSearchKeyDown}
              placeholder="Search…"
              className="w-full pl-9 pr-14 py-2 text-sm bg-slate-100 dark:bg-slate-800 rounded-lg border-0 focus:ring-2 focus:ring-indigo-500 focus:outline-none"
            />
            {searchLoading && (
              <Loader size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 animate-spin" />
            )}
            {!searchLoading && searchQuery && (
              <button onClick={clearSearch} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                <X size={14} />
              </button>
            )}
            {!searchLoading && !searchQuery && (
              <kbd
                className="hidden sm:inline-flex absolute right-2 top-1/2 -translate-y-1/2 items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-mono rounded bg-white dark:bg-slate-900 text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-slate-700 pointer-events-none"
                aria-hidden="true"
                title="Press ⌘K (or Ctrl+K) anywhere to open the command palette"
              >
                ⌘K
              </kbd>
            )}

            {/* Results dropdown */}
            {searchResults && (
              <div onMouseDown={(e) => e.preventDefault()} className="absolute top-full left-0 right-0 mt-1.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl z-50 overflow-hidden max-h-96 overflow-y-auto">
                {!hasResults ? (
                  <p className="px-4 py-6 text-sm text-center text-slate-400">No results for "{searchQuery}"</p>
                ) : (
                  <div className="divide-y divide-slate-100 dark:divide-slate-800">
                    {/* Matters */}
                    {searchResults.matters.length > 0 && (
                      <div>
                        <p className="px-4 pt-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">Case Files</p>
                        {searchResults.matters.map((m) => {
                          const clientName = m.client
                            ? [m.client.first_name, m.client.last_name].filter(Boolean).join(' ') || m.client.organization
                            : '';
                          return (
                            <button
                              key={m.id}
                              onClick={() => goToResult(`/matters/${m.id}`)}
                              className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-indigo-50 dark:hover:bg-indigo-500/10 transition text-left"
                            >
                              <div className="w-7 h-7 rounded-md bg-indigo-100 dark:bg-indigo-500/20 flex items-center justify-center flex-shrink-0">
                                <Briefcase size={13} className="text-indigo-600 dark:text-indigo-400" />
                              </div>
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-slate-800 dark:text-slate-200 truncate">{m.title || m.matter_number}</p>
                                <p className="text-xs text-slate-400 truncate">{[m.matter_number, clientName].filter(Boolean).join(' · ')}</p>
                              </div>
                              <span className={classNames(
                                'ml-auto flex-shrink-0 text-[10px] font-medium px-2 py-0.5 rounded-full',
                                m.status === 'active'   ? 'bg-green-100  text-green-700  dark:bg-green-900/30  dark:text-green-400'
                                : m.status === 'closed' ? 'bg-slate-100  text-slate-500  dark:bg-slate-800     dark:text-slate-400'
                                :                         'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                              )}>{m.status}</span>
                            </button>
                          );
                        })}
                      </div>
                    )}

                    {/* Contacts */}
                    {searchResults.contacts.length > 0 && (
                      <div>
                        <p className="px-4 pt-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">Contacts</p>
                        {searchResults.contacts.map((c) => {
                          const name = [c.first_name, c.last_name].filter(Boolean).join(' ') || c.organization || c.email;
                          return (
                            <button
                              key={c.id}
                              onClick={() => goToResult(`/contacts/${c.id}`)}
                              className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-indigo-50 dark:hover:bg-indigo-500/10 transition text-left"
                            >
                              <div className="w-7 h-7 rounded-md bg-sky-100 dark:bg-sky-500/20 flex items-center justify-center flex-shrink-0">
                                <Users size={13} className="text-sky-600 dark:text-sky-400" />
                              </div>
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-slate-800 dark:text-slate-200 truncate">{name}</p>
                                <p className="text-xs text-slate-400 truncate">{[c.contact_type, c.email].filter(Boolean).join(' · ')}</p>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    )}

                    {/* Documents */}
                    {searchResults.documents.length > 0 && (
                      <div>
                        <p className="px-4 pt-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">Documents</p>
                        {searchResults.documents.map((d) => (
                          <button
                            key={d.id}
                            onClick={() => goToResult(d.matter_id ? `/matters/${d.matter_id}` : '/documents')}
                            className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-indigo-50 dark:hover:bg-indigo-500/10 transition text-left"
                          >
                            <div className="w-7 h-7 rounded-md bg-amber-100 dark:bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                              <FileText size={13} className="text-amber-600 dark:text-amber-400" />
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-slate-800 dark:text-slate-200 truncate">{d.name}</p>
                              <p className="text-xs text-slate-400 truncate">{d.category || 'Document'}</p>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="ml-auto flex items-center gap-3">
            {/* Aggregated notifications — uploads to review, announcements, deadlines */}
            <NotificationBell />
            {/* Avatar button — clickable for employees to go to their own profile */}
            {myEmployeeId ? (
              <button
                onClick={() => navigate(`/employees/${myEmployeeId}`)}
                title="My Profile"
                className="hidden sm:flex items-center gap-2 rounded-lg px-2 py-1 hover:bg-slate-100 dark:hover:bg-slate-800 transition"
              >
                <div className="w-8 h-8 rounded-full bg-indigo-100 dark:bg-indigo-500/20 text-indigo-700 dark:text-indigo-300 text-xs font-semibold flex items-center justify-center overflow-hidden flex-shrink-0">
                  {myPhotoUrl
                    ? <img src={myPhotoUrl} alt="" className="w-full h-full object-cover" />
                    : initials(userProfile || {})
                  }
                </div>
                <div className="text-xs text-left">
                  <p className="font-medium">{fullName(userProfile) || 'You'}</p>
                  <p className="text-slate-500 dark:text-slate-400">{userProfile?.role || ''}</p>
                </div>
              </button>
            ) : (
              <div className="hidden sm:flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-indigo-100 dark:bg-indigo-500/20 text-indigo-700 dark:text-indigo-300 text-xs font-semibold flex items-center justify-center">
                  {initials(userProfile || {})}
                </div>
                <div className="text-xs">
                  <p className="font-medium">{fullName(userProfile) || 'You'}</p>
                  <p className="text-slate-500 dark:text-slate-400">{userProfile?.role || ''}</p>
                </div>
              </div>
            )}
          </div>
        </header>

        <TrialBanner />
        {/* pb-16 on mobile gives clearance above the bottom tab bar */}
        {/* Bottom padding has to clear the fixed mobile tab bar (~64px)
            PLUS the iPhone home-indicator inset, otherwise the last
            element on every page hides behind the nav. calc() with
            env() handles both in one shot. On lg+ the desktop sidebar
            takes over and the tab bar is hidden, so we drop back to
            a comfortable 24px. */}
        <main className="p-4 sm:p-6 pb-[calc(5rem+env(safe-area-inset-bottom,0px))] lg:pb-6">{children}</main>
      </div>

      {/* ── Mobile bottom tab bar ── */}
      <nav className="fixed bottom-0 left-0 right-0 z-40 lg:hidden bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-800 flex safe-area-pb">
        {BOTTOM_TABS.map((tab) => {
          const Icon   = tab.icon;
          const active = activePath(tab.path);
          const isTimer = tab.special === 'timer';
          const timerRunning = isTimer && timer.running;
          return (
            <button
              key={tab.path}
              onClick={() => navigate(tab.path)}
              className={classNames(
                'flex-1 flex flex-col items-center justify-center py-2 gap-0.5 text-[10px] font-medium transition-colors relative',
                active
                  ? 'text-indigo-600 dark:text-indigo-400'
                  : timerRunning
                  ? 'text-emerald-600 dark:text-emerald-400'
                  : 'text-slate-500 dark:text-slate-400',
              )}
            >
              <div className="relative">
                <Icon size={22} strokeWidth={active || timerRunning ? 2.5 : 1.75} />
                {timerRunning && (
                  <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                )}
              </div>
              <span className={timerRunning ? 'font-mono tabular-nums' : ''}>
                {isTimer ? timerLabel : tab.label}
              </span>
            </button>
          );
        })}
        {/* "More" opens the full sidebar drawer */}
        <button
          onClick={() => setMobileOpen(true)}
          className={classNames(
            'flex-1 flex flex-col items-center justify-center py-2 gap-0.5 text-[10px] font-medium transition-colors',
            mobileOpen ? 'text-indigo-600 dark:text-indigo-400' : 'text-slate-500 dark:text-slate-400',
          )}
        >
          <Menu size={22} strokeWidth={1.75} />
          More
        </button>
      </nav>

      {showUpgrade && <UpgradeModal onClose={() => setShowUpgrade(false)} />}
    </div>
  );
}
